import type { GridSelection } from './geometry'

// Why the selection lives in the browser: the drag is local, the pin is not.
// The browser owns the rectangle and the extracted text (it already holds every
// cell), while the server owns only the buffer-view pin that keeps those lines
// from scrolling away underneath it.

export interface GridSelectionCell {
  readonly text: string
}

export interface GridSelectionGrid {
  readonly cols: number
  readonly rows: number
  readonly cells: readonly GridSelectionCell[]
}

export interface GridSelectionTransport {
  sendGridSelectionBegin(resizeGeneration: number, anchorCol: number, anchorRow: number): void
  sendGridSelectionClear(resizeGeneration: number): void
}

export type GridSelectionInvalidation =
  | 'resize'
  | 'scroll'
  | 'ownership'
  | 'reconnect'

export interface GridSelectionOptions {
  readonly transport: GridSelectionTransport
  readonly grid: () => GridSelectionGrid
  readonly resizeGeneration: () => number
  readonly writeClipboard?: (text: string) => Promise<void>
  readonly onClipboardError?: (message: string) => void
  readonly onChange?: () => void
}

export function normaliseSelection(
  anchorCol: number, anchorRow: number, currentCol: number, currentRow: number,
): GridSelection {
  return {
    active: true,
    col0: Math.min(anchorCol, currentCol),
    row0: Math.min(anchorRow, currentRow),
    col1: Math.max(anchorCol, currentCol),
    row1: Math.max(anchorRow, currentRow),
  }
}

// Trailing whitespace is trimmed per row exactly as lera_sel_extract does:
// `<= ' '` rather than `== ' '`, because a control byte in a cell would
// otherwise reach the clipboard. UTF-8 continuation bytes never compare low,
// and JS code units for astral scalars are surrogates, so no character is cut.
function trimRowEnd(row: string): string {
  let end = row.length
  while (end > 0 && row.charCodeAt(end - 1) <= 0x20) end--
  return row.slice(0, end)
}

// Mirrors lera_sel_extract: inclusive rect clamped to the grid, empty cells
// become a single space, rows join with '\n', and a selection whose rows all
// trim away reports nothing so the caller leaves the clipboard alone.
export function extractSelection(grid: GridSelectionGrid, rect: GridSelection): string {
  if (!rect.active) return ''
  const { cols, rows, cells } = grid
  if (cols <= 0 || rows <= 0) return ''
  const col1 = Math.min(rect.col1, cols - 1)
  const row1 = Math.min(rect.row1, rows - 1)
  if (rect.col0 > col1 || rect.row0 > row1 || rect.col0 < 0 || rect.row0 < 0) return ''

  const lines: string[] = []
  let anyContent = false
  for (let row = rect.row0; row <= row1; row++) {
    let line = ''
    for (let col = rect.col0; col <= col1; col++) {
      const text = cells[row * cols + col]?.text ?? ''
      line += text === '' ? ' ' : text
    }
    line = trimRowEnd(line)
    if (line.length > 0) anyContent = true
    lines.push(line)
  }
  return anyContent ? lines.join('\n') : ''
}

export class GridSelectionController {
  readonly #transport: GridSelectionTransport
  readonly #grid: () => GridSelectionGrid
  readonly #resizeGeneration: () => number
  readonly #writeClipboard: (text: string) => Promise<void>
  readonly #onClipboardError?: (message: string) => void
  readonly #onChange?: () => void
  #rect: GridSelection | null = null
  #anchorCol = 0
  #anchorRow = 0
  // The generation the pin was taken under. A clear has to name that one, not
  // whatever the grid has become since.
  #pinnedGeneration = 0

  constructor(options: GridSelectionOptions) {
    this.#transport = options.transport
    this.#grid = options.grid
    this.#resizeGeneration = options.resizeGeneration
    this.#writeClipboard = options.writeClipboard ??
      ((text: string) => navigator.clipboard.writeText(text))
    this.#onClipboardError = options.onClipboardError
    this.#onChange = options.onChange
  }

  // GridPointerSelection -----------------------------------------------------

  begin(col: number, row: number): void {
    this.#anchorCol = col
    this.#anchorRow = row
    this.#rect = normaliseSelection(col, row, col, row)
    this.#pin()
    this.#onChange?.()
  }

  extend(col: number, row: number): void {
    if (this.#rect === null) return
    this.#rect = normaliseSelection(this.#anchorCol, this.#anchorRow, col, row)
    this.#onChange?.()
  }

  // Pointer up ends the drag, not the selection: the pin stays so the copied
  // text keeps matching what is highlighted.
  finish(col: number, row: number): void {
    this.extend(col, row)
  }

  cancel(): void {
    this.#clear(true)
  }

  // GridInputSelection -------------------------------------------------------

  hasSelection(): boolean {
    return this.#rect !== null
  }

  copySelection(): boolean {
    const rect = this.#rect
    if (rect === null) return false
    const text = extractSelection(this.#grid(), rect)
    // An entirely blank selection leaves the existing clipboard alone.
    if (text === '') return false
    void this.#writeClipboard(text).catch((error: unknown) => {
      // A refused clipboard must not disturb the selection or its pin.
      this.#onClipboardError?.(error instanceof Error ? error.message : String(error))
    })
    return true
  }

  clearSelection(): boolean {
    if (this.#rect === null) return false
    this.#clear(true)
    return true
  }

  // Lifecycle ----------------------------------------------------------------

  rect(): GridSelection | null {
    return this.#rect
  }

  // The grid the selection was measured on is gone, so the rectangle no longer
  // describes anything; the server still has to be told to drop the pin.
  invalidate(_reason: GridSelectionInvalidation): void {
    this.#clear(true)
  }

  // The server already dropped the pin, so echoing a clear would only be a
  // second, stale message about an interaction that is already over.
  invalidateFromServer(): void {
    this.#clear(false)
  }

  #pin(): void {
    if (this.#pinnedGeneration !== 0) return
    const generation = this.#resizeGeneration()
    if (generation <= 0) return
    this.#pinnedGeneration = generation
    this.#transport.sendGridSelectionBegin(generation, this.#anchorCol, this.#anchorRow)
  }

  #clear(notifyServer: boolean): void {
    const had = this.#rect !== null || this.#pinnedGeneration !== 0
    const generation = this.#pinnedGeneration
    this.#rect = null
    this.#pinnedGeneration = 0
    if (notifyServer && generation !== 0) {
      this.#transport.sendGridSelectionClear(generation)
    }
    if (had) this.#onChange?.()
  }
}
